import { Injectable } from '@angular/core';
import { ReportPayload, ReportRecord } from '../models/report-payload';

import { ReportCategory } from '../models/report-category';
import { VehicleReportData, VehicleSummary } from '../models/report-viewers';

import { reportExportPermissions } from '../models/report-export';
import { ReportFormatterService } from './report-formatter.service';
import { ReportTableColumn } from '../models/report-table';

import { Columns } from '../configs/report-columns.config';
import { NgxSpinnerService } from 'ngx-spinner';

import { VehicleSummaryHeaders } from '../models/report-viewers';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ReportType } from '../models/report-types';
import { ChecklistEventService } from 'src/app/events/services/checklist-event.service';
import { cleanKmLabel } from '../helpers/cleanKmLabel';
import { setExcelDateTimeFormat, setExcelDateTimeFormatFromValue } from '../helpers/setExcelDateTimeFormat';

@Injectable({
  providedIn: 'root'
})
export class ReportExportService {

  public headerColor = "1F4E78";

  constructor(
    private reportFormatterService: ReportFormatterService,
    private spinner: NgxSpinnerService,
    private checklist: ChecklistEventService
  ) {
    this.getColorTable();
  }

  public applyExportPermissions(report: ReportPayload) {
    const reportType = report.reportDetails.reportType;
    const permission = reportExportPermissions[reportType];

    if (permission) {
      if (!report.permission) {
        report.permission = {};
      }

      if (!report.permission.export) {
        report.permission.export = { excel: '', pdf: '', html: '' };
      }

      report.permission.export.html = permission.html;
      report.permission.export.excel = permission.excel;
      report.permission.export.pdf = permission.pdf;
    }
  }
  public async exportVehicleReport(reportPayload: ReportPayload, format: string) {
    this.spinner.show("exportReponnerSpinner");
    try {
      const handlers: Record<string, () => Promise<void> | void> = {
        excel: async () => this.exportVehicleReportDataToExcel(reportPayload, reportPayload.dataViewer),
        html: () => this.exportVehiclesReportToHtml(reportPayload, reportPayload.dataViewer),
        pdf: () => this.exportVehiclesReportDataToPdf(reportPayload, reportPayload.dataViewer),
      };

      const handler = handlers[format];
      if (handler) {
        await handler();
      } else {
        console.warn("Tipo de exportación no soportado:", format);
      }
    } finally {
      this.spinner.hide("exportReponnerSpinner");
    }
  }
  public async exportReport(reportPayload: ReportPayload, format: string) {
    this.spinner.show("exportReponnerSpinner");
    const { reportType } = reportPayload.reportDetails;

    try {
      const handlers: Record<string, () => Promise<void> | void> = {
        excel: async () => {
          if (reportType === ReportType.REPORTE_CHECKLIST) {
            return this.checklist.exportChecklistExcelBrowserWithImage(reportPayload, this.headerColor);
          }
          return this.exportToExcel(reportPayload);
        },
        html: () => {
          if (reportType === ReportType.REPORTE_MONITOREO_CANTIDAD_EVENTOS_POR_UNIDAD) {
            return this.exportEventByUnitHtml(reportPayload);
          }
          return this.exportToHtml(reportPayload);
        },
        pdf: () => this.exportToPdf(reportPayload),
      };

      const handler = handlers[format];
      if (handler) {
        await handler();
      } else {
        console.warn("Tipo de exportación no soportado:", format);
      }
    } catch (e: any) {
      console.log("Report Export ", e)
    }
    finally {
      this.spinner.hide("exportReponnerSpinner");
    }
  }


  public async exportToExcel(reportPayload: ReportPayload) {
    const exportHandlers: Record<ReportCategory | string, (payload: ReportPayload) => void> = {
      [ReportCategory.VEHICLE]: (payload) => {
        const records = this.reportFormatterService.collectVehicleRecordsFromReport(payload.dataViewer);
        this.exportReportRecordsToExcel(payload, records);
      },
      [ReportCategory.BASE]: (payload) => {
        const records = payload.dataViewer.records || []
        this.exportReportRecordsToExcel(payload, records);
      },
    }
    const handler = exportHandlers[reportPayload.reportDetails.category || ReportCategory.DEPRECATED];
    if (handler) {
      handler(reportPayload);
    } else {
      console.warn('No export handler defined for category', reportPayload.reportDetails.category);
    }
  }
  public async exportToPdf(reportPayload: ReportPayload) {
    const exportHandlers: Record<ReportCategory | string, (payload: ReportPayload) => void> = {
      [ReportCategory.VEHICLE]: (payload) => {
        const records = this.reportFormatterService.collectVehicleRecordsFromReport(payload.dataViewer);
        this.exportReportRecordsToPdf(payload, records);
      },
      [ReportCategory.BASE]: (payload) => {
        const records = payload.dataViewer.records || []
        this.exportReportRecordsToPdf(payload, records);
      },
    }
    const handler = exportHandlers[reportPayload.reportDetails.category || ReportCategory.DEPRECATED];
    if (handler) {
      handler(reportPayload);
    } else {
      console.warn('No export handler defined for category', reportPayload.reportDetails.category);
    }
  }
  public async exportToHtml(reportPayload: ReportPayload) {
    const exportHandlers: Record<ReportCategory | string, (payload: ReportPayload) => void> = {
      [ReportCategory.VEHICLE]: (payload) => {
        const records = this.reportFormatterService.collectVehicleRecordsFromReport(payload.dataViewer);
        this.exportReportRecordsToHtml(payload, records);
      },
      [ReportCategory.BASE]: (payload) => {
        const records = payload.dataViewer.records || []
        this.exportReportRecordsToHtml(payload, records);
      },
    }
    const handler = exportHandlers[reportPayload.reportDetails.category || ReportCategory.DEPRECATED];
    if (handler) {
      handler(reportPayload);
    } else {
      console.warn('No export handler defined for category', reportPayload.reportDetails.category);
    }
  }

  public exportReportRecordsToExcel(
    reportPayload: ReportPayload,
    records: ReportRecord[],
    titleReport?: string,
    summary?: VehicleSummary
  ) {
    const columns = this.prepareColumnsForExport(reportPayload.columns || []);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reporte');

    let withFormatText = false;
    if (reportPayload.reportDetails.reportType === ReportType.REPORTE_EVENTOS || reportPayload.reportDetails.reportType === ReportType.REPORTE_EVENTOS_INTERNO) {
      withFormatText = true;
    }

    let useDefaultDash = true;
    if (
      reportPayload.reportDetails.reportType === ReportType.REPORTE_EXCESOS_VELOCIDAD_DURACION_TOLERANCIA_NIVEL_RIESGO ||
      reportPayload.reportDetails.reportType === ReportType.REPORTE_EXCESOS_VELOCIDAD_DIRECCION_DURACION_TOLERANCIA_NIVEL_RIESGO ||
      reportPayload.reportDetails.reportType === ReportType.REPORTE_EXCESOS_VELOCIDAD_POR_NIVEL
    ) {
      useDefaultDash = false;
      // reportNivel = true;
    }

    // ----------------------
    // 1. TÍTULO DEL REPORTE
    // ----------------------
    worksheet.mergeCells(1, 1, 1, this.getLeafColumnsCount(columns));
    const titleCell = worksheet.getCell(1, 1);
    const baseTitle = reportPayload.reportDetails.title || '';
    const title = titleReport ? `${titleReport} - ${baseTitle}` : baseTitle;
    const events = reportPayload.eventConfig?.selectedEventNames;
    titleCell.value = events ? `${title}${events}` : title;
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
    worksheet.getRow(1).height = 30;

    // ----------------------
    // 2. PERIODO DEL REPORTE
    // ----------------------
    worksheet.mergeCells(2, 1, 2, this.getLeafColumnsCount(columns));
    const cellPeriodo = worksheet.getCell(2, 1);
    cellPeriodo.value = "Período";
    cellPeriodo.font = { size: 12, color: { argb: 'FFFFFFFF' } };
    cellPeriodo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'A6A6A6' } };
    cellPeriodo.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(2).height = 20;

    // ----------------------
    // 3. RANGO DE FECHAS
    // ----------------------
    worksheet.mergeCells(3, 1, 3, this.getLeafColumnsCount(columns));
    const cellPeriodoDates = worksheet.getCell(3, 1);
    cellPeriodoDates.value = reportPayload.reportDetails.periodRange;
    cellPeriodoDates.font = { size: 12 };
    cellPeriodoDates.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEECE1' } };
    cellPeriodoDates.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(3).height = 20;

    // ----------------------
    // 4. ENCABEZADOS DE LA TABLA
    // ----------------------
    const startRow = 4;

    const writeHeaders = (cols: ReportTableColumn[], row: number, colStart: number): number => {
      let col = colStart;
      cols.forEach(colDef => {
        if (colDef.subColumns?.length) {
          const colSpan = writeHeaders(colDef.subColumns, row + 1, col) - col;
          worksheet.mergeCells(row, col, row, col + colSpan - 1);
          const headerCell = worksheet.getCell(row, col);
          headerCell.value = colDef.header;
          this.applyHeaderStyle(headerCell);
          col += colSpan;
        } else {
          const headerCell = worksheet.getCell(row, col);
          headerCell.value = colDef.header;
          this.applyHeaderStyle(headerCell);

          if (colDef.width)
            worksheet.getColumn(col).width = Math.round(
              parseInt(colDef.width.replace(/[^0-9]/g, '')) / 7
            );

          // Fusión vertical si no tiene subcolumnas y hay más niveles por debajo
          const maxLevel = this.getMaxHeaderLevel(columns);
          if (row < startRow + maxLevel - 1) {
            worksheet.mergeCells(row, col, row + maxLevel - 1, col);
          }

          col++;
        }
      });
      return col;
    };

    writeHeaders(columns, startRow, 1);

    // ----------------------
    // 5. DATOS
    // ----------------------
    const dataStartRow = startRow + this.getMaxHeaderLevel(columns);

    records.forEach((record, rowIndex) => {
      let colIndex = 1;

      const writeData = (cols: ReportTableColumn[], rowNum: number, colStart: number): number => {
        let col = colStart;
        cols.forEach((colDef, i) => {
          if (colDef.subColumns?.length) {
            col = writeData(colDef.subColumns, rowNum, col);
          } else {
            const cell = worksheet.getCell(rowNum, col);
            if (colDef.type === 'link' && record[colDef.field as keyof ReportRecord]) {
              if (colDef.field == "ubicacion") {
                cell.value = {
                  text: `${record[colDef.field as keyof ReportRecord] ?? '-'}`,
                  formula: `HYPERLINK("${record.ubicacionLink as string}", "${record[colDef.field as keyof ReportRecord] ?? '-'}")`,
                };
              } else {
                cell.value = {
                  text: colDef.header,
                  formula: `HYPERLINK("${record[colDef.field as keyof ReportRecord]}", "${colDef.header}")`,
                };
              }
              cell.font = { color: { argb: 'FF0000FF' }, underline: true };
            } else if (colDef.type == 'date') {
              let value = record[colDef.field as keyof ReportRecord] ?? '';
              setExcelDateTimeFormat(cell, String(value), useDefaultDash);
            } else if (colDef.field == "operadorMonitoreo") {
              let operadorMonitoreo = record[colDef.field as keyof ReportRecord] || '-';
              if (operadorMonitoreo == "OTRO") {
                let otroOperadorMonitoreo = record['otroOperadorMonitoreo'] || '-';
                cell.value = otroOperadorMonitoreo;
              } else {
                cell.value = operadorMonitoreo;
              }
            } else {
              let value = record[colDef.field as keyof ReportRecord] ?? '';
              // aplicar limpieza solo si es string
              if (typeof value === 'string') {
                if (colDef.field == "observacion" || colDef.field == "motivo") {
                  value = value || "-";
                } else {
                  if (colDef.type == "number") {
                    value = cleanKmLabel(value, useDefaultDash);
                  }
                }
              }
              if (colDef.type != 'number' && withFormatText) {
                console.log("report excel")

                  cell.numFmt = '@';
                }
                cell.value = value;

            }

            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            col++;
          }
        });
        return col;
      };

      writeData(columns, dataStartRow + rowIndex, colIndex);
      // --- aplicar estilo a la fila si se cumple la condición ---
      if (record.isResumen === true) {
        const rowExcel = worksheet.getRow(dataStartRow + rowIndex);
        rowExcel.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E5E3D9' }
          };
        });
      }
    });

    // ----------------------
    // 5.1 RESUMEN si existe
    // ----------------------
    if (summary) {
      let currentRow = dataStartRow + records.length + 1; // fila siguiente a los datos

      // Título del resumen
      worksheet.mergeCells(currentRow, 1, currentRow, this.getLeafColumnsCount(columns));
      const summaryTitleCell = worksheet.getCell(currentRow, 1);
      summaryTitleCell.value = 'RESUMEN DE TOTALES';
      summaryTitleCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      summaryTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
      summaryTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Valores del resumen
      Object.entries(summary)
        .filter(([_, v]) => v !== undefined && v !== null && v !== '')
        .forEach(([key, value]) => {
          worksheet.mergeCells(currentRow, 1, currentRow, this.getLeafColumnsCount(columns));
          const cell = worksheet.getCell(currentRow, 1);
          const label = VehicleSummaryHeaders[key] ?? key;
          cell.value = `${label}: ${value}`;
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
          currentRow++;
        });
    }


    // ----------------------
    // 6. DESCARGAR EXCEL
    // ----------------------
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const fileName = `${reportPayload.reportDetails.nameFile}.xlsx`;
      FileSaver.saveAs(blob, fileName);
    });
  }
  public exportReportRecordsToPdf(reportPayload: ReportPayload, records: ReportRecord[], titleReport?: string, summary?: VehicleSummary) {
    // Preparar columnas
    let columns = this.prepareColumnsForExport(reportPayload.columns || []);

    const doc = new jsPDF({ orientation: 'landscape' });

    // -------------------------
    // Información del reporte
    // -------------------------
    const lines: string[] = [];
    const baseTitle = reportPayload.reportDetails.title || '';
    let title = titleReport ? `${titleReport} - ${baseTitle}` : baseTitle;
    const events = reportPayload.eventConfig?.selectedEventNames;
    const subtitle = reportPayload.reportDetails.subtitle;

    if (events) title += ` ${events}`;
    if (subtitle) title += ` - ${subtitle}`;
    lines.push(`Reporte: ${title}`);

    if (reportPayload.reportDetails.periodRange) {
      lines.push(`Periodo: ${reportPayload.reportDetails.periodRange}`);
    }

    doc.setFontSize(9).setTextColor(0, 0, 0).setFont("helvetica", 'normal');
    let startY = 10;
    lines.forEach(line => { doc.text(line, 14, startY); startY += 6; });

    // -------------------------
    // Aplanar columnas
    // -------------------------
    function flattenColumns(cols: ReportTableColumn[]): ReportTableColumn[] {
      let result: ReportTableColumn[] = [];
      cols.forEach(col => {
        if (col.subColumns && col.subColumns.length > 0) {
          result = result.concat(flattenColumns(col.subColumns));
        } else {
          result.push(col);
        }
      });
      return result;
    }

    const flatColumns = flattenColumns(columns);

    // --- Construir headers dinámicamente ---
    const headers = flatColumns.map(col => ({ header: col.header, dataKey: col.field, type: col.type }));

    const data = records.map((row, idx) => {
      const obj: Record<string, any> = {};
      flatColumns.forEach(col => {
        if (col.field === 'item') {
          obj[col.field] = idx + 1; // excepción: columna Item numerada
        } else if (col.type === 'link') {
          obj[col.field] = row[col.field] ?? '';
        } else if (col.field == "operadorMonitoreo") {
          let operador = row[col.field] || '-';
          if (operador == "OTRO") {
            let otroOperador = row['otroOperadorMonitoreo'] || '-';
            obj[col.field] = otroOperador;
          } else {
            obj[col.field] = operador;
          }
        } else {
          obj[col.field] = row[col.field] === null || row[col.field] === undefined || row[col.field] === ""
            ? '-'
            : row[col.field];

        }
      });
      return obj;
    });

    // Estilos de columnas
    const maxColWidth = 30;
    const columnStyles: Record<string, any> = {};
    // headers.forEach(h => columnStyles[h.dataKey] = { cellWidth: maxColWidth });
    headers.forEach(h => {

      if (h.dataKey === 'ubicacion') {

        columnStyles[h.dataKey] = {
          cellWidth: Math.max(40, maxColWidth) // mínimo 40
        };

      } else {

        columnStyles[h.dataKey] = {
          cellWidth: maxColWidth
        };

      }

    });

    autoTable(doc, {
      startY: startY ,
      head: [headers.map(h => h.header)],
      body: data.map(row => headers.map(h => row[h.dataKey])),
      styles: { fontSize: 6, overflow: 'linebreak' },
      headStyles: { fillColor: this.headerColor, textColor: 255, halign: 'center', fontStyle: 'bold' },
      columnStyles,
      margin: { top: 10, left: 10, right: 10, bottom: 10 },

      didParseCell: (cellData) => {
        if (cellData.section === 'body') {
          const colDef = headers[cellData.column.index];
          const row = records[cellData.row.index];
          if (!row) return;

          // --- ESTILO POR FILA ---
          if (row.isResumen === true) {
            cellData.cell.styles.fillColor = [229, 227, 217];
          }

          // Reducir texto largo
          let text = cellData.cell.text as string | string[];
          let length = typeof text === 'string' ? text.length : Array.isArray(text) ? text.reduce((acc, line) => acc + line.length, 0) : 0;
          if (length > 40) {
            cellData.cell.styles.fontSize = 5;
            if (typeof text === 'string' && text.length > 100) cellData.cell.text = [text.substring(0, 100) + '...'];
            if (Array.isArray(text)) cellData.cell.text = text.map(line => line.length > 100 ? line.substring(0, 100) + '...' : line);
          }

          if (colDef.type === "link") {
            cellData.cell.styles.textColor = [0, 0, 255];
            console.log(cellData.cell.text, colDef.header);
            if (colDef.header == 'Imagen') cellData.cell.text = ['Imagen'];
            if (colDef.header == 'Video') cellData.cell.text = ['Video'];
            if (colDef.header == 'Archivo') cellData.cell.text = ['Archivo'];
          }
          // Vaciar contenido original para links
        }
      },

      didDrawCell: (cellData) => {
        if (cellData.section === 'body') {
          const colDef = headers[cellData.column.index];
          const row = records[cellData.row.index];
          if (!row) return;

          if (colDef.type === 'link') {
            let url: string;

            if (colDef.dataKey === "ubicacion") {
              url = String(row.ubicacionLink);
            } else {
              url = String(row[colDef.dataKey]);
            }

            doc.link(
              cellData.cell.x,
              cellData.cell.y,
              cellData.cell.width,
              cellData.cell.height,
              { url }
            );
          }
        }
      }
    });


    // -------------------------
    // 4. Agregar resumen si existe
    // -------------------------
    if (summary) {
      let currentY = (doc as any).lastAutoTable?.finalY ?? startY + 10;

      const pageHeight = doc.internal.pageSize.height;

      const summaryEntries = Object.entries(summary)
        .filter(([_, v]) => v !== undefined && v !== null && v !== '');

      const blockHeight = (summaryEntries.length + 1) * 5 + 12;

      if (currentY + blockHeight > pageHeight - 10) {
        doc.addPage();
        currentY = 15;
      }
      // Título del resumen
      doc.setFontSize(8).setFont("helvetica", 'bold').setTextColor(255, 255, 255);
      doc.setFillColor(0, 51, 102); // azul oscuro
      doc.rect(10, currentY, doc.internal.pageSize.getWidth() - 20, 7, 'F');
      doc.text('RESUMEN DE TOTALES', 12, currentY + 5);
      currentY += 10;

      doc.setFontSize(7).setFont("helvetica", 'normal').setTextColor(0, 0, 0);

      Object.entries(summary)
        .filter(([_, v]) => v !== undefined && v !== null && v !== '')
        .forEach(([key, value]) => {
          const label = VehicleSummaryHeaders[key] ?? key;
          doc.text(`${label}: ${value}`, 12, currentY);
          currentY += 5;
        });
    }

    doc.save(`${reportPayload.reportDetails.nameFile || 'Reporte'}.pdf`);
  }
  public exportReportRecordsToHtml(reportPayload: ReportPayload, rows: ReportRecord[], titleReport?: string, summary?: VehicleSummary) {
    const columns = this.prepareColumnsForExport(reportPayload.columns || []);

    // Construir headers para HTML (solo las columnas definidas)
    const headers = columns.map(col => col.header);

    // Construir filas de datos
    const rowsHTML = rows.map((row, index) => {
      const rowStyle = row.isResumen === true ? 'background-color:#E5E3D9;' : '';

      const cells = columns.map(col => {
        if (col.type === 'link' && row[col.field]) {
          const linkText = col.field === 'ubicacion' ? row[col.field] : col.header;
          return `<td><a href="${row.ubicacionLink as string}" target="_blank" style="color:blue; text-decoration: underline;">${linkText}</a></td>`;
        }
        // Excepción: si la columna es "item", mostrar índice + 1
        if (col.field === 'item') {
          return `<td>${index + 1}</td>`;
        }
        if (col.field == "operadorMonitoreo") {
          let operador = row[col.field] || '-'
          if (operador == "OTRO") {
            let otroOperador = row["otroOperadorMonitoreo"] || "-";
            return `<td>${otroOperador || '-'}</td>`;
          } else {
            return `<td>${operador || '-'}</td>`;
          }
        }
        return `<td>${row[col.field] ?? '-'}</td>`;
      });
      return `<tr style="${rowStyle}">${cells.join('')}</tr>`;
    }).join('');

    // Construir título completo
    const baseTitle2 = reportPayload.reportDetails.title || 'Reporte';
    const baseTitle = titleReport ? `${titleReport} - ${baseTitle2}` : baseTitle2;

    const events = reportPayload.eventConfig?.selectedEventNames;
    const subtitle = reportPayload.reportDetails.subtitle;

    let fullTitle = baseTitle;
    if (events) fullTitle += ` ${events}`;
    if (subtitle) fullTitle += ` - ${subtitle}`;


    // Construir HTML del summary si existe
    let summaryHTML = '';
    if (summary) {
      summaryHTML += `<h3 style="margin-top:20px; background-color:#${this.headerColor}; color:white; padding:5px;">RESUMEN DE TOTALES</h3><table style="border-collapse: collapse; width: 50%; font-family: Arial, sans-serif;">`;
      Object.entries(summary)
        .filter(([_, v]) => v !== undefined && v !== null && v !== '')
        .forEach(([key, value]) => {
          const label = VehicleSummaryHeaders[key] ?? key;
          summaryHTML += `<tr><td style="border:1px solid #${this.headerColor}; padding:5px; font-weight:bold;">${label}</td><td style="border:1px solid #${this.headerColor}; padding:5px;">${value}</td></tr>`;
        });
      summaryHTML += `</table>`;
    }
    // HTML completo
    const htmlContent = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8" />
      <title>${fullTitle}</title>
      <style>
        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
        th, td { border: 1px solid #${this.headerColor}; padding: 8px; text-align: center; }
        th { background-color: #${this.headerColor}; color: white; font-weight: bold; }
        tr:nth-child(even) { background-color: #f2f2f2; }
      </style>
    </head>
    <body>
      <h2 style="text-align:center; color:#${this.headerColor};">${fullTitle}</h2>
      <table>
        <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${rowsHTML}</tbody>
      </table>
      ${summaryHTML}
    </body>
    </html>
    `;

    // Descargar HTML
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportPayload.reportDetails.nameFile || 'Reporte'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  public exportVehicleReportDataToExcel(
    reportPayload: ReportPayload,
    vehicleReportData: VehicleReportData,
    singleSheet: boolean = true
  ) {
    if (reportPayload.reportDetails.reportType === ReportType.REPORTE_MONITOREO_CANTIDAD_EVENTOS_POR_UNIDAD) {
      return this.exportEventByUnitExcel(reportPayload, singleSheet);
    }
    let withFormatText = false;
    if (reportPayload.reportDetails.reportType === ReportType.REPORTE_EVENTOS || reportPayload.reportDetails.reportType === ReportType.REPORTE_EVENTOS_INTERNO) {
      withFormatText = true;
    }

    const workbook = new ExcelJS.Workbook();
    const columns = this.prepareColumnsForExport(reportPayload.columns || []);

    // ----------------------
    // Función para agregar tabla de un vehículo
    // ----------------------
    const addVehicleTable = (
      worksheet: ExcelJS.Worksheet,
      vehicleData: { vehicle: { name: string; imei: string }, records: ReportRecord[], summary?: VehicleSummary },
      startRow: number
    ): number => {
      let currentRow = startRow;

      // --- Nombre del vehículo ---
      worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
      const vehicleCell = worksheet.getCell(currentRow, 1);
      vehicleCell.value = vehicleData.vehicle.name;
      vehicleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      vehicleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
      vehicleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // --- Encabezados ---
      let colIndex = 1;
      columns.forEach(col => {
        const cell = worksheet.getCell(currentRow, colIndex);
        cell.value = col.header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        if (col.width) {
          const pixels = parseInt(col.width.replace(/[^0-9]/g, ''), 10);
          worksheet.getColumn(colIndex).width = Math.round(pixels / 7);
        }
        colIndex++;
      });
      currentRow++;

      // --- Datos ---
      vehicleData.records.forEach((record, index) => {
        colIndex = 1;
        columns.forEach((col) => {
          const cell = worksheet.getCell(currentRow, colIndex);

          if (col.type === 'link' && record[col.field as keyof ReportRecord]) {
            if (col.field === "ubicacion") {
              cell.value = {
                text: `${record[col.field as keyof ReportRecord] ?? '-'}`,
                formula: `HYPERLINK("${record.ubicacionLink as string}", "${record[col.field as keyof ReportRecord] ?? '-'}")`
              };
            } else {
              cell.value = {
                text: col.header,
                formula: `HYPERLINK("${record[col.field as keyof ReportRecord] ?? ''}", "${col.header}")`
              };
            }
            cell.font = { color: { argb: 'FF0000FF' }, underline: true };
          } else if (col.type == 'date') {
            let value = record[col.field as keyof ReportRecord] ?? '';
            setExcelDateTimeFormat(cell, String(value));
          } else {
            if (col.field === "item") {
              cell.value = (index + 1).toString() ?? '';
            } else {
              let value = record[col.field as keyof ReportRecord] ?? '';
              if (typeof value === 'string') {
                if (col.type == "number") {
                  value = cleanKmLabel(value);
                }
              }
              if (col.type != 'number' && withFormatText) {
                console.log("report excel")
                cell.numFmt = '@';
              }
              cell.value = value;
            }
          }

          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          colIndex++;
        });
        currentRow++;
      });

      // --- Resumen si existe ---
      if (vehicleData.summary) {
        currentRow++; // fila en blanco
        worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
        const summaryTitleCell = worksheet.getCell(currentRow, 1);
        summaryTitleCell.value = 'RESUMEN DE TOTALES';
        summaryTitleCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        summaryTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
        summaryTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(currentRow).height = 20;
        currentRow++;

        Object.entries(vehicleData.summary)
          .filter(([_, v]) => v !== undefined && v !== null && v !== '')
          .forEach(([key, value]) => {
            worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
            const cell = worksheet.getCell(currentRow, 1);
            const label = VehicleSummaryHeaders[key] ?? key;
            cell.value = `${label}: ${value}`;
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            currentRow++;
          });
      }

      return currentRow + 2; // espacio antes del siguiente vehículo
    };

    // ----------------------
    // Función para agregar encabezado general del reporte
    // ----------------------
    const addReportHeader = (worksheet: ExcelJS.Worksheet): number => {
      let currentRow = 1;

      // Título del reporte + eventos
      worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
      const titleCell = worksheet.getCell(currentRow, 1);
      const title = reportPayload.reportDetails.title || '';
      const events = reportPayload.eventConfig?.selectedEventNames;
      titleCell.value = events ? `${title}${events}` : title;
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
      worksheet.getRow(currentRow).height = 30;
      currentRow++;

      // Subtítulo "Período"
      worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
      const periodSubtitleCell = worksheet.getCell(currentRow, 1);
      periodSubtitleCell.value = "Período";
      periodSubtitleCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      periodSubtitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'A6A6A6' } };
      periodSubtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(currentRow).height = 20;
      currentRow++;

      // Rango de fechas
      worksheet.mergeCells(currentRow, 1, currentRow, columns.length);
      const periodCell = worksheet.getCell(currentRow, 1);
      periodCell.value = reportPayload.reportDetails.periodRange;
      periodCell.font = { size: 12 };
      periodCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEECE1' } };
      periodCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(currentRow).height = 20;
      currentRow += 2;

      // Congelar filas hasta aquí
      worksheet.views = [{ state: 'frozen', ySplit: currentRow - 1 }];

      return currentRow;
    };

    // ----------------------
    // Generación de hojas
    // ----------------------
    if (singleSheet) {
      const worksheet = workbook.addWorksheet('Reporte Vehículos');
      let currentRow = addReportHeader(worksheet);
      vehicleReportData.vehicles.forEach(v => {
        currentRow = addVehicleTable(worksheet, v, currentRow);
      });
    } else {
      vehicleReportData.vehicles.forEach(v => {
        const worksheet = workbook.addWorksheet(v.vehicle.name.slice(0, 31));
        let currentRow = addReportHeader(worksheet);
        addVehicleTable(worksheet, v, currentRow);
      });
    }

    // ----------------------
    // Descargar archivo Excel
    // ----------------------
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      FileSaver.saveAs(
        blob,
        `${reportPayload.reportDetails.nameFile || 'Reporte_Vehiculos'}.xlsx`
      );
    });
  }
  public exportVehiclesReportDataToPdf(
    reportPayload: ReportPayload,
    vehicleReportData: VehicleReportData
  ) {
    const doc = new jsPDF({ orientation: 'landscape' });
    const columns = this.prepareColumnsForExport(reportPayload.columns || []);

    function flattenColumns(cols: ReportTableColumn[]): ReportTableColumn[] {
      let result: ReportTableColumn[] = [];
      cols.forEach(col => {
        if (col.subColumns && col.subColumns.length > 0) {
          result = result.concat(flattenColumns(col.subColumns));
        } else {
          result.push(col);
        }
      });
      return result;
    }

    const flatColumns = flattenColumns(columns);

    // --- Headers sin columna Item ---
    const headers = flatColumns.map(col => ({
      header: col.header,
      dataKey: col.field,
      type: col.type
    }));

    let currentY = 10;

    vehicleReportData.vehicles.forEach((vehicleData, idx) => {
      // --- Encabezado ---
      let title = reportPayload.reportDetails.title || 'Reporte';
      const events = reportPayload.eventConfig?.selectedEventNames;
      const subtitle = reportPayload.reportDetails.subtitle;
      if (events) title += ` ${events}`;
      if (subtitle) title += ` - ${subtitle}`;

      doc.setFontSize(9).setTextColor(0, 0, 0).setFont("helvetica", "normal");
      doc.text(`Reporte: ${title}`, 14, currentY);
      currentY += 6;

      if (reportPayload.reportDetails.periodRange) {
        doc.text(`Periodo: ${reportPayload.reportDetails.periodRange}`, 14, currentY);
        currentY += 6;
      }

      // --- Nombre del vehículo ---
      doc.setFontSize(11).setFont("helvetica", "bold").setTextColor(0, 0, 128);
      doc.text(`Vehículo: ${vehicleData.vehicle.name}`, 14, currentY);
      currentY += 8;

      // --- Datos del vehículo ---
      const data = vehicleData.records.map((row, index) => {
        const obj: Record<string, any> = {};
        flatColumns.forEach(col => {
          if (col.field === "item") {
            obj[col.field] = index + 1; // número de fila
          } else if (col.type === "link" && row[col.field]) {
            // obj[col.field] = col.header; // texto visible para links
            obj[col.field] = row[col.field] ?? "";
          } else {
            obj[col.field] = row[col.field] ?? "";
          }
        });
        return obj;
      });

      const columnStyles: Record<string, any> = {};
      const maxColWidth = 30;
      headers.forEach(h => columnStyles[h.dataKey] = { cellWidth: maxColWidth });

      headers.forEach(h => {

        if (h.dataKey === 'ubicacion') {

          columnStyles[h.dataKey] = {
            cellWidth: Math.max(40, maxColWidth) // mínimo 40
          };

        } else if(h.dataKey === 'parametros') {
          columnStyles[h.dataKey] = {
            cellWidth: 45 // o 70 si quieres más compacto
          };
        } else {

          columnStyles[h.dataKey] = {
            cellWidth: maxColWidth
          };

        }

      });

      autoTable(doc, {
        startY: currentY,
        head: [headers.map(h => h.header)],
        body: data.map(row => headers.map(h => row[h.dataKey])),
        styles: { fontSize: 6, overflow: "linebreak" },
        headStyles: { fillColor: this.headerColor, textColor: 255, halign: "center", fontStyle: "bold" },
        columnStyles,
        margin: { top: 10, left: 10, right: 10, bottom: 10 },

        didParseCell: cellData => {
          if (cellData.section === "body") {
            const colDef = headers[cellData.column.index];
            const row = vehicleData.records[cellData.row.index];
            if (!row) return;

            let text = cellData.cell.text as string | string[];
            let length = 0;
            if (typeof text === "string") length = text.length;
            else if (Array.isArray(text)) length = text.reduce((acc, line) => acc + line.length, 0);

            if (length > 40) {
              cellData.cell.styles.fontSize = 5;
              if (typeof text === "string" && text.length > 100) cellData.cell.text = [text.substring(0, 100) + "..."];
              if (Array.isArray(text)) cellData.cell.text = text.map(line => line.length > 100 ? line.substring(0, 100) + "..." : line);
            }

            if (colDef.type === "link") {
              cellData.cell.styles.textColor = [0, 0, 255];
              if (colDef.header == 'Imagen') cellData.cell.text = ['Imagen'];
              if (colDef.header == 'Video') cellData.cell.text = ['Video'];
              if (colDef.header == 'Archivo') cellData.cell.text = ['Archivo'];
            }
          }
        },

        didDrawCell: cellData => {
          if (cellData.section === "body") {
            const colDef = headers[cellData.column.index];
            const row = vehicleData.records[cellData.row.index];
            if (!row) return;

            if (colDef.type === "link" && row[colDef.dataKey]) {
              let url: string;

              if (colDef.dataKey === "ubicacion") {
                url = String(row.ubicacionLink);
              } else {
                url = String(row[colDef.dataKey]);
              }

              doc.link(
                cellData.cell.x,
                cellData.cell.y,
                cellData.cell.width,
                cellData.cell.height,
                { url }
              );
            }
          }
        }
      });

      currentY = (doc as any).lastAutoTable.finalY + 10;
      // const separation = 6;
      // currentY += separation;

      // --- Resumen por vehículo ---
      const pageHeight = doc.internal.pageSize.height;
      if (vehicleData.summary) {

        const summaryEntries = Object.entries(vehicleData.summary)
          .filter(([_, v]) => v !== undefined && v !== null && v !== "");

        const blockHeight = (summaryEntries.length + 1) * 6 + 10;

        if (currentY + blockHeight > pageHeight - 10) {
          doc.addPage();
          currentY = 15;
        }

        doc.setFontSize(10)
          .setFont("helvetica", "bold")
          .setTextColor(0, 0, 0);
        doc.text("Resumen de Totales:", 14, currentY);
        currentY += 6;


        doc.setFontSize(9).setFont("helvetica", "normal");

         summaryEntries.forEach(([key, value]) => {
          const label = VehicleSummaryHeaders[key] ?? key;
          doc.text(`${label}: ${value}`, 14, currentY);
          currentY += 6;
        });

        currentY += 10;

        // Object.entries(vehicleData.summary)
        //   .filter(([_, v]) => v !== undefined && v !== null && v !== "")
        //   .forEach(([key, value]) => {
        //     const label = VehicleSummaryHeaders[key] ?? key;
        //     doc.text(`${label}: ${value}`, 14, currentY);
        //     currentY += 6;
        //   });

        // Object.entries(vehicleData.summary)
        //   .filter(([_, v]) => v !== undefined && v !== null && v !== "")
        //   .forEach(([key, value]) => {

        //     const label = VehicleSummaryHeaders[key] ?? key;
        //     const text = `${label}: ${value}`;

        //     // 🔴 AQUÍ EVITAS QUE SE SALGA
        //     if (currentY > pageHeight - 20) {
        //       doc.addPage();
        //       currentY = 15;
        //     }

        //     doc.text(text, 14, currentY);
        //     currentY += 6;
        // });

        // currentY += 10;
      }

      if (idx < vehicleReportData.vehicles.length - 1 && currentY > doc.internal.pageSize.height - 40) {
        doc.addPage();
        currentY = 10;
      }
    });

    doc.save(`${reportPayload.reportDetails.nameFile || "Reporte_Vehiculos"}.pdf`);
  }
  public exportVehiclesReportToHtml(
    reportPayload: ReportPayload,
    vehicleReportData: VehicleReportData
  ) {
    const columns = this.prepareColumnsForExport(reportPayload.columns || []);

    // Headers HTML (sin Item)
    const headers = [...columns.map(col => col.header)];

    // Título completo
    const baseTitle = reportPayload.reportDetails.title || 'Reporte';
    const events = reportPayload.eventConfig?.selectedEventNames;
    const subtitle = reportPayload.reportDetails.subtitle;
    let fullTitle = baseTitle;
    if (events) fullTitle += ` ${events}`;
    if (subtitle) fullTitle += ` - ${subtitle}`;

    // Secciones de cada vehículo
    const vehicleSections = vehicleReportData.vehicles.map(vehicleData => {
      const vehicle = vehicleData.vehicle;

      // Filas de datos (sin Item)
      const rowsHTML = vehicleData.records.map((row, index) => {
        const cells = columns.map(col => {
          if (col.field === 'item') {
            const item = index + 1;
            return `<td>${item ?? ''}</td>`;
          }
          if (col.type === 'link' && row[col.field]) {
            const linkText = col.field === 'ubicacion' ? row[col.field] : col.header;
            return `<td><a href="${row.ubicacionLink as string}" target="_blank" style="color:blue; text-decoration:underline;">${linkText}</a></td>`;
          }
          return `<td>${row[col.field] ?? ''}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      // Resumen opcional
      let summaryHTML = '';
      if (vehicleData.summary) {
        const summaryRows = Object.entries(vehicleData.summary)
          .filter(([_, v]) => v !== undefined && v !== null && v !== '')
          .map(([key, value]) => {
            const label = VehicleSummaryHeaders[key] ?? key;
            return `<tr><td style="font-weight:bold;">${label}</td><td>${value}</td></tr>`;
          }).join('');

        summaryHTML = `
          <h4 style="color:#${this.headerColor}; margin-top:15px;">Resumen de Totales</h4>
          <table>
            <thead><tr><th>Campo</th><th>Valor</th></tr></thead>
            <tbody>${summaryRows}</tbody>
          </table>
        `;
      }

      return `
        <h3 style="color:#${this.headerColor}; margin-top:30px;">Vehículo: ${vehicle.name}</h3>
        <table>
          <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
          <tbody>${rowsHTML}</tbody>
        </table>
        ${summaryHTML}
      `;
    }).join('');

    // HTML completo
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <title>${fullTitle}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h2 { text-align:center; color:#${this.headerColor}; }
          h3 { margin-bottom: 10px; }
          h4 { margin-bottom: 5px; }
          table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
          th, td { border: 1px solid #${this.headerColor}; padding: 6px; font-size: 12px; }
          th { background-color: #${this.headerColor}; color: white; font-weight: bold; text-align: center; }
          td { text-align: center; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          a { color: blue; text-decoration: underline; }
        </style>
      </head>
      <body>
        <h2>${fullTitle}</h2>
        ${reportPayload.reportDetails.periodRange
          ? `<p style="text-align:center;"><b>Periodo:</b> ${reportPayload.reportDetails.periodRange}</p>`
          : ''}
        ${vehicleSections}
      </body>
      </html>
    `;

    // Descargar HTML
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportPayload.reportDetails.nameFile || 'Reporte_Vehiculos'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }


  // ----------------------
  // Reportes Personalizados
  // ----------------------
  public exportEventByUnitExcel(reportPayload: ReportPayload, singleSheet: boolean) {
    const workbook = new ExcelJS.Workbook();

    if (singleSheet) {
      // ================================
      // VERSIÓN 1: TODO EN UNA SOLA HOJA
      // ================================
      const worksheet = workbook.addWorksheet("Reporte Consolidado");

      let currentRow = 1;

      reportPayload.data?.forEach((report: any) => {
        const reportInfo = report[0];
        const reportData = report[1];
        const totalColumns = reportData[0].length;

        // 1. Título
        worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
        const titleCell = worksheet.getCell(currentRow, 1);
        titleCell.value = reportInfo.name;
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.headerColor } };
        worksheet.getRow(currentRow).height = 30;
        currentRow++;

        // 2. Período
        worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
        const periodoCell = worksheet.getCell(currentRow, 1);
        periodoCell.value = "Período";
        periodoCell.alignment = { horizontal: "center", vertical: "middle" };
        periodoCell.font = { size: 12, color: { argb: "FFFFFFFF" } };
        periodoCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
        worksheet.getRow(currentRow).height = 20;
        currentRow++;

        // 3. Rango de fechas
        worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
        const rangoCell = worksheet.getCell(currentRow, 1);
        rangoCell.value = reportPayload.reportDetails?.periodRange || "";
        rangoCell.alignment = { horizontal: "center", vertical: "middle" };
        rangoCell.font = { size: 12 };
        rangoCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "EEECE1" } };
        worksheet.getRow(currentRow).height = 20;
        currentRow++;

        // 4. Encabezados
        const headerRow = worksheet.addRow(reportData[0]);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.headerColor } };
        });
        currentRow++;

        // 5. Datos
        reportData.slice(1).forEach((row: any[]) => {
          const dataRow = worksheet.addRow(row);
          dataRow.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            if (typeof cell.value === "number" && cell.value > 0) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEB9C" } };
            }
          });
          currentRow++;
        });

        // Espacio entre reportes
        currentRow += 2;
      });

      // Ajuste ancho columnas
      worksheet.columns?.forEach((col) => (col.width = 14));
    } else {
      // ================================
      // VERSIÓN 2: UNA HOJA POR REPORTE
      // ================================
      reportPayload.data?.forEach((report: any) => {
        const reportInfo = report[0];
        const reportData = report[1];
        const worksheet = workbook.addWorksheet(reportInfo.name);
        const totalColumns = reportData[0].length;

        // 1. Título
        worksheet.mergeCells(1, 1, 1, totalColumns);
        const titleCell = worksheet.getCell(1, 1);
        titleCell.value = reportInfo.name;
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.headerColor } };
        worksheet.getRow(1).height = 30;

        // 2. Período
        worksheet.mergeCells(2, 1, 2, totalColumns);
        const periodoCell = worksheet.getCell(2, 1);
        periodoCell.value = "Período";
        periodoCell.alignment = { horizontal: "center", vertical: "middle" };
        periodoCell.font = { size: 12, color: { argb: "FFFFFFFF" } };
        periodoCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "A6A6A6" } };
        worksheet.getRow(2).height = 20;

        // 3. Rango de fechas
        worksheet.mergeCells(3, 1, 3, totalColumns);
        const rangoCell = worksheet.getCell(3, 1);
        rangoCell.value = reportPayload.reportDetails?.periodRange || "";
        rangoCell.alignment = { horizontal: "center", vertical: "middle" };
        rangoCell.font = { size: 12 };
        rangoCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "EEECE1" } };
        worksheet.getRow(3).height = 20;

        // 4. Encabezados
        const headerRow = worksheet.addRow(reportData[0]);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: this.headerColor } };
        });

        // 5. Datos
        reportData.slice(1).forEach((row: any[]) => {
          const dataRow = worksheet.addRow(row);
          dataRow.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            if (typeof cell.value === "number" && cell.value > 0) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEB9C" } };
            }
          });
        });

        // Ajuste ancho columnas
        worksheet.columns?.forEach((col) => (col.width = 14));
      });
    }

    // ================================
    // DESCARGA DEL ARCHIVO
    // ================================
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `${reportPayload.reportDetails?.nameFile || "reportes"}.xlsx`;
      FileSaver.saveAs(blob, fileName);
    });
  }
  public exportEventByUnitHtml(reportPayload: ReportPayload) {
    // Construir contenido HTML
    let htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <title>${reportPayload.reportDetails?.title || 'Reporte'}</title>
        <style>
          table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; margin-bottom: 2rem; }
          th, td { border: 1px solid #${this.headerColor}; padding: 8px; text-align: center; }
          th { background-color: #${this.headerColor}; color: white; font-weight: bold; }
          tr:nth-child(even) { background-color: #f2f2f2; }
          h2 { text-align: center; color: #${this.headerColor}; }
        </style>
      </head>
      <body>
    `;

    reportPayload.data?.forEach((report: any) => {
      const reportInfo = report[0]; // {id, name, slug}
      const reportData = report[1]; // [["Codigo", "Placa", "Fecha"], [...], ...]

      // Título de cada reporte
      htmlContent += `<h2>${reportInfo.name}</h2>`;

      // Encabezados de la tabla
      const headers = reportData[0].map((h: any) => `<th>${h}</th>`).join('');

      // Filas de datos
      const rowsHTML = reportData.slice(1).map((row: any[]) => {
        const cells = row.map(cell => `<td>${cell ?? ''}</td>`);
        return `<tr>${cells.join('')}</tr>`;
      }).join('');

      // Construir tabla
      htmlContent += `
        <table>
          <thead><tr>${headers}</tr></thead>
          <tbody>${rowsHTML}</tbody>
        </table>
      `;
    });

    htmlContent += `
      </body>
      </html>
    `;

    // Descargar HTML
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportPayload.reportDetails?.nameFile || 'Reporte'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }


  // ----------------------
  // FUNCIONES AUXILIARES
  // ----------------------
  public prepareColumnsForExport(columns: ReportTableColumn[]): ReportTableColumn[] {
    return columns.reduce<ReportTableColumn[]>((acc, col) => {
      switch (col.field) {
        case 'enlaceArchivo':
          // Reemplazar por Imagen y Video
          acc.push(Columns.enlaceImagen, Columns.enlaceVideo);
          break;
        default:
          // Columna normal, sin cambios
          acc.push(col);
          break;
      }
      return acc;
    }, []);
  }
  private applyHeaderStyle(cell: ExcelJS.Cell) {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: this.headerColor } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '000000' } },
      bottom: { style: 'thin', color: { argb: '000000' } },
      right: { style: 'thin', color: { argb: '000000' } }
    };
  }

  // cuenta todas las columnas hojas para fusionar título
  private getLeafColumnsCount(columns: ReportTableColumn[]): number {
    return columns.reduce((sum, col) => col.subColumns?.length ? sum + this.getLeafColumnsCount(col.subColumns) : sum + 1, 0);
  }

  // calcula el nivel máximo de encabezados (para fusion vertical)
  private getMaxHeaderLevel(columns: ReportTableColumn[]): number {
    if (!columns || columns.length === 0) return 0;
    return 1 + Math.max(...columns.map(c => this.getMaxHeaderLevel(c.subColumns || [])));
  }


  public getColorTable() {
    const root = document.documentElement;
    const color = getComputedStyle(root).getPropertyValue("--gl-primary");
    this.headerColor = color.trim().replace("#", "");
  }

}
